import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../../shared/theme';
import { s_GC } from '../../shared/styles';
import { useStorage } from '../../shared/hooks';
import { trackSocialEvent, togglePostLike, notifyUser, sharePostApi } from '../../shared/dbService';
import { playSound } from '../../shared/soundEngine';
import { timeAgo, getInitials } from '../../shared/utils';
import Avatar from '../../components/ui/Avatar';
import PhotoLightbox from '../../components/ui/PhotoLightbox';
import ProfilePage from './ProfilePage';
import FriendsScreen from './FriendsScreen';
import ChatScreen from './ChatScreen';
import FocusGroupsTab from './FocusGroupsTab';
import ScoresTab from './ScoresTab';
import PostComposer from './PostComposer';
import TechLoadingBar from '../../components/ui/TechLoadingBar';

const BASE = process.env.REACT_APP_API_URL || '/api';

const FEELINGS = [
  { emoji: '🔥', label: 'Motivated', labelNL: 'Gemotiveerd' },
  { emoji: '💪', label: 'Proud', labelNL: 'Trots' },
  { emoji: '😌', label: 'Calm', labelNL: 'Kalm' },
  { emoji: '🧘', label: 'Focused', labelNL: 'Gefocust' },
  { emoji: '🎉', label: 'Excited', labelNL: 'Enthousiast' },
  { emoji: '😰', label: 'Struggling', labelNL: 'Aan het worstelen' },
  { emoji: '😔', label: 'Tired', labelNL: 'Moe' },
  { emoji: '❤️', label: 'Grateful', labelNL: 'Dankbaar' },
];

// ── Resonance bar: viewer indicates how much they share the poster's feeling ──
function ResonanceBar({ postId, feeling, postUserId, currentUserId, lang, initialMyValue, initialAvg, initialCount, onUpdate }) {
  const nl = lang === 'nl';
  const f = FEELINGS.find(x => x.label === feeling?.label) || feeling;
  const [myValue, setMyValue] = useState(initialMyValue ?? null);
  const [avg, setAvg] = useState(initialAvg ?? null);
  const [count, setCount] = useState(initialCount ?? 0);
  const [saved, setSaved] = useState(initialMyValue !== null && initialMyValue !== undefined);

  const handleCommit = async (val) => {
    setMyValue(val);
    setSaved(true);
    const uid = currentUserId;
    try {
      const r = await fetch(`${BASE}/posts/${uid}/${postId}/resonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: val }),
      });
      if (r.ok) {
        const data = await r.json();
        setAvg(data.avg);
        setCount(data.count);
        if (onUpdate) onUpdate(postId, { resonanceAvg: data.avg, resonanceCount: data.count, myResonance: val });
      }
    } catch {}
    const weight = Math.max(1, Math.round(val / 25));
    trackSocialEvent('post_resonance', { targetId: postUserId, weight });
  };

  return (
    <div style={{ marginBottom: 10, padding: '12px 14px', background: `${_T.accent}0d`, borderRadius: 12, border: `1px solid ${_T.accent}30` }}>
      <div style={{ fontSize: 12, color: C.silver2, marginBottom: 10, fontWeight: 500 }}>
        {nl ? 'Herken jij dit gevoel?' : 'Do you share this feeling?'}
        {avg !== null && (
          <span style={{ float: 'right', fontSize: 11, color: _T.accent, fontWeight: 700 }}>
            ⌀ {avg}% · {count} {nl ? (count === 1 ? 'persoon' : 'mensen') : (count === 1 ? 'person' : 'people')}
          </span>
        )}
      </div>

      {/* Average bar */}
      {avg !== null && (
        <div style={{ height: 4, background: `${_T.accent}20`, borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ height: '100%', width: `${avg}%`, background: `linear-gradient(90deg,${_T.grad1},${_T.grad2})`, borderRadius: 4, transition: 'width 0.6s ease' }} />
        </div>
      )}

      {/* My slider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10, color: C.silver }}>0%</span>
        <input
          type="range" min={0} max={100}
          value={myValue ?? 0}
          onChange={e => setMyValue(Number(e.target.value))}
          onMouseUp={e => handleCommit(Number(e.target.value))}
          onTouchEnd={e => handleCommit(Number(e.target.value))}
          style={{ flex: 1, accentColor: _T.accent, cursor: 'pointer' }}
        />
        <span style={{ fontSize: 10, color: C.silver }}>100%</span>
        <span style={{ fontSize: 13, color: saved ? _T.accent : C.silver, fontWeight: 700, minWidth: 38, textAlign: 'right' }}>
          {myValue !== null ? `${myValue}%` : '--'}
        </span>
      </div>
      {saved && (
        <div style={{ fontSize: 9, color: _T.accent, marginTop: 5 }}>
          ✓ {nl ? 'Opgeslagen · Empathie groeit' : 'Saved · Empathy grows'}
        </div>
      )}
    </div>
  );
}

// ── Comment component with @mention support ───────────────────────────────────
function CommentBox({ postId, currentUserId, userName, friends, allUsers, lang, nl, open }) {
  const [comments, setComments] = useStorage(`sl_comments_${postId}`, []);
  const [text, setText] = useState('');
  const [mentionQ, setMentionQ] = useState('');   // text after the last @
  const [mentionPos, setMentionPos] = useState(null); // index of @ in text string
  const [pendingNotify, setPendingNotify] = useState([]); // users to notify on submit
  const inputRef = useRef(null);

  const avatarImg = (() => {
    try { return localStorage.getItem(`sl_avatar_${currentUserId}`) || localStorage.getItem('sl_google_picture') || null; } catch { return null; }
  })();

  // All other users as mention candidates — no fragile ID mapping needed
  const searchableUsers = (allUsers || []).filter(u => u.id !== currentUserId && u.name);

  const handleInput = (e) => {
    const val = e.target.value;
    setText(val);
    // Match @ followed by optional word chars at the end of what the user typed (mobile-safe)
    const match = val.match(/@(\w*)$/);
    if (match) {
      setMentionQ(match[1].toLowerCase());
      setMentionPos(val.length - match[0].length);
    } else {
      setMentionQ('');
      setMentionPos(null);
    }
  };

  // Show all users on bare @, filter as more chars are typed
  const mentionMatches = mentionPos !== null
    ? (mentionQ.length > 0
        ? searchableUsers.filter(u => u.name.toLowerCase().includes(mentionQ))
        : searchableUsers
      ).slice(0, 5)
    : [];

  const insertMention = (user) => {
    if (mentionPos === null) return;
    const before = text.slice(0, mentionPos);
    const after = text.slice(mentionPos + 1 + mentionQ.length);
    const newText = `${before}@${user.name} ${after}`;
    setText(newText);
    setMentionQ('');
    setMentionPos(null);
    // Track this user for push notification on submit
    setPendingNotify(prev => prev.find(u => u.id === user.id) ? prev : [...prev, user]);
    inputRef.current?.focus();
  };

  const submit = async () => {
    if (!text.trim()) return;
    const comment = {
      id: Date.now(),
      userId: currentUserId,
      name: userName,
      text: text.trim(),
      time: new Date().toISOString(),
    };
    setComments(prev => [...prev, comment]);
    const snapshot = pendingNotify;
    setText(''); setMentionQ(''); setMentionPos(null); setPendingNotify([]);
    // Notify each tagged friend
    for (const mUser of snapshot) {
      if (mUser.id !== currentUserId) {
        notifyUser(mUser.id, 'post_comment', userName, comment.text.slice(0, 80)).catch(() => {});
      }
    }
  };

  const renderCommentText = (t) => {
    // Highlight @mentions
    const parts = t.split(/(@[\w\s]+?)(?=\s|$)/g);
    return parts.map((part, i) =>
      part.startsWith('@')
        ? <span key={i} style={{ color: _T.accent, fontWeight: 600 }}>{part}</span>
        : part
    );
  };

  return (
    <div>
      {open && (
        <div style={{ paddingBottom: 8 }}>
          {/* Existing comments */}
          {comments.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', background: `${_T.accent}20`, border: `1px solid ${_T.accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: _T.accent, flexShrink: 0 }}>
                {getInitials(c.name || '?')}
              </div>
              <div style={{ flex: 1, background: _T.bg4, borderRadius: 10, padding: '7px 10px' }}>
                <div style={{ fontSize: 11, color: _T.accent, fontWeight: 600, marginBottom: 2 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: C.silver2, lineHeight: 1.5 }}>{renderCommentText(c.text)}</div>
                <div style={{ fontSize: 9, color: `${C.silver}88`, marginTop: 3 }}>{timeAgo(c.time, lang)}</div>
              </div>
            </div>
          ))}

          {/* Comment input + floating @mention dropdown */}
          <div style={{ position: 'relative' }}>
            {/* Floating mention list — appears above the input */}
            {mentionMatches.length > 0 && (
              <div style={{ position: 'absolute', bottom: '100%', left: 34, right: 38, marginBottom: 4, background: _T.bg2, border: `1px solid ${_T.accent}44`, borderRadius: 12, overflow: 'hidden', zIndex: 50, boxShadow: '0 -4px 20px rgba(0,0,0,0.4)' }}>
                {mentionMatches.map(u => (
                  <button key={u.id} onMouseDown={e => { e.preventDefault(); insertMention(u); }}
                    style={{ width: '100%', background: 'none', border: 'none', borderBottom: `1px solid ${_T.cardBorderBot}`, padding: '9px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: `${u.color || _T.accent}22`, border: `1px solid ${u.color || _T.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: u.color || _T.accent, flexShrink: 0 }}>{getInitials(u.name)}</div>
                    <span style={{ color: C.white, fontSize: 13, fontWeight: 500 }}>@{u.name}</span>
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {avatarImg
                ? <img src={avatarImg} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 26, height: 26, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: _T.accent, flexShrink: 0 }}>{getInitials(userName || 'Me')}</div>
              }
              <input
                ref={inputRef}
                value={text}
                onChange={handleInput}
                placeholder={nl ? 'Reageer... typ @ om te taggen' : 'Comment... type @ to tag'}
                style={{ flex: 1, background: _T.bg4, border: `1px solid ${mentionPos !== null ? _T.accent : _T.cardBorderBot}`, borderRadius: 20, padding: '8px 12px', color: C.white, fontSize: 12, outline: 'none', fontFamily: 'Inter,sans-serif', transition: 'border-color 0.2s' }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
              />
              <button onClick={submit} disabled={!text.trim()} style={{ background: _T.accent, border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: text.trim() ? 'pointer' : 'default', opacity: text.trim() ? 1 : 0.4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>↑</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CommunityScreen({ lang, userName, currentUserId, allUsers, friends, friendRequests, onFriendRequest, onAcceptFriend, onDeclineFriend, conversations, setConversations, myPosts, setMyPosts, onAddNotification, initialTab, onPlayTogether, onNav, onViewBizPage }) {
  const nl = lang === "nl";
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab) return initialTab;
    try { return localStorage.getItem('sl_last_community_tab') || "timeline"; } catch { return "timeline"; }
  });
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => { if (initialTab) setActiveTab(initialTab); }, [initialTab]);
  useEffect(() => { try { localStorage.setItem('sl_last_community_tab', activeTab); } catch {} }, [activeTab]);
  const [profileTarget, setProfileTarget] = useState(null);
  const [liked, setLiked] = useStorage("sl_liked", []);
  const [feedPosts, setFeedPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [openCommentPosts, setOpenCommentPosts] = useState(new Set());
  const toggleComments = (postId) => setOpenCommentPosts(prev => { const n = new Set(prev); n.has(postId) ? n.delete(postId) : n.add(postId); return n; });
  const [openResonancePosts, setOpenResonancePosts] = useState(new Set());
  const toggleResonance = (postId) => setOpenResonancePosts(prev => { const n = new Set(prev); n.has(postId) ? n.delete(postId) : n.add(postId); return n; });
  const handleResonanceUpdate = (postId, updates) => setFeedPosts(prev => prev.map(p => p.id === postId ? { ...p, ...updates } : p));
  const [shareTarget, setShareTarget] = useState(null); // post being shared
  const [shareText, setShareText]     = useState('');
  const [sharing, setSharing]         = useState(false);
  const handleShare = async () => {
    if (!shareTarget) return;
    setSharing(true);
    await sharePostApi(shareTarget.id, shareText);
    setSharing(false);
    setShareTarget(null);
    setShareText('');
    // Refresh feed
    const url = currentUserId ? `${BASE}/posts?uid=${encodeURIComponent(currentUserId)}` : `${BASE}/posts`;
    fetch(url).then(r => r.ok ? r.json() : []).then(d => { if (Array.isArray(d)) setFeedPosts(d); });
  };

  const currentUser = allUsers.find(u => u.id === currentUserId) || { id: currentUserId, name: userName, color: _T.accent, isResting: false };

  useEffect(() => {
    const url = currentUserId ? `${BASE}/posts?uid=${encodeURIComponent(currentUserId)}` : `${BASE}/posts`;
    fetch(url)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return;
        setFeedPosts(data);
        setLiked(data.filter(p => p.likedByMe).map(p => p.id));
      })
      .catch(() => {})
      .finally(() => setFeedLoading(false));
  }, [currentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openProfile = u => { setProfileTarget(u); setActiveTab("profile"); };

  // Handle new post from composer
  const handleNewPost = (p) => {
    const newPost = {
      id: Date.now(),
      userId: currentUserId,
      text: p.text || '',
      img: p.img || null,
      likes: 0,
      time: new Date().toISOString(),
      isPublic: p.isPublic !== false ? 1 : 0,
      feeling: p.feeling || null,
      user: { id: currentUserId, name: userName, picture: null, color: _T.accent },
    };
    setFeedPosts(prev => [newPost, ...prev]);
    // Also sync to sl_my_posts so it appears on personal timeline
    if (setMyPosts) {
      setMyPosts(prev => [{ id: newPost.id, text: newPost.text, img: newPost.img, isPublic: newPost.isPublic, time: 'just now', likes: 0, feeling: newPost.feeling }, ...prev]);
    }
    trackSocialEvent('post_created', { weight: 3 });

    // Notify friends when posting publicly
    if (newPost.isPublic && friends && friends.length > 0) {
      friends.forEach(friendId => {
        notifyUser(friendId, 'post_created', userName, p.text ? p.text.slice(0, 80) : '📸').catch(() => {});
      });
    }
  };

  const chatUnreadCount = Object.values(conversations || {}).reduce((sum, msgs) =>
    sum + (Array.isArray(msgs) ? msgs.filter(m => m.from !== currentUserId && !m.read).length : 0), 0);

  const TABS = [
    { id: "timeline", icon: "📰", label: nl ? "Feed" : "Feed" },
    { id: "friends_tab", icon: "👥", label: nl ? "Vrienden" : "Friends" },
    { id: "chat", icon: "💬", label: nl ? "Chat" : "Chat", badge: chatUnreadCount },
    { id: "groups", icon: "🧩", label: nl ? "Groepen" : "Groups" },
    { id: "scores", icon: "🏆", label: "Scores" },
  ];

  const tabBar = (
    <div style={{ display: "flex", background: _T.bg4, borderRadius: 14, padding: 4, marginBottom: 14, gap: 2 }}>
      {TABS.map(t => (
        <div key={t.id} style={{ flex: 1, position: "relative" }}>
          <button onClick={() => { playSound('tab'); setActiveTab(t.id); }} style={{ width: "100%", background: activeTab === t.id ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : "transparent", border: "none", borderRadius: 10, padding: "9px 4px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <span style={{ fontSize: 16 }}>{t.icon}</span>
            <span style={{ fontSize: 9, color: activeTab === t.id ? "#0a1220" : C.silver, fontWeight: activeTab === t.id ? 700 : 400 }}>{t.label}</span>
          </button>
          {t.badge > 0 && (
            <div style={{ position: "absolute", top: 2, right: 4, minWidth: 16, height: 16, borderRadius: 8, background: "#FF3B30", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff", padding: "0 3px", pointerEvents: "none", lineHeight: 1 }}>{t.badge > 99 ? "99+" : t.badge}</div>
          )}
        </div>
      ))}
    </div>
  );

  const header = null;

  const timeline = (
    <div>
      <PostComposer lang={lang} userName={userName} currentUserId={currentUserId} onPost={handleNewPost} />
      {feedLoading && <TechLoadingBar lang={lang} />}
      {!feedLoading && feedPosts.length === 0 && (
        <div style={{ ...s_GC({ textAlign: "center", padding: 24 }), color: C.silver, fontSize: 13 }}>{nl ? "Nog geen berichten." : "No posts yet."}</div>
      )}
      {feedPosts.map(p => {
        const u = p.user || (p.userId === currentUserId ? currentUser : (allUsers || []).find(x => x.id === p.userId)) || { name: "?", color: _T.accent };
        const isLiked = liked.includes(p.id);
        return (
          <div key={p.id} style={s_GC({ padding: 14, marginBottom: 8 })}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
              <Avatar user={u} size={36} onClick={() => openProfile(u)} />
              <div style={{ flex: 1 }}>
                <div style={{ color: C.white, fontSize: 13, fontWeight: 600, cursor: "pointer" }} onClick={() => openProfile(u)}>{u.name}</div>
                <div style={{ color: C.silver, fontSize: 11 }}>{timeAgo(p.time, lang)}</div>
              </div>
              <button onClick={() => { setShareTarget(p); setShareText(''); }}
                style={{ background: "none", border: "none", color: C.silver, cursor: "pointer", padding: "4px 6px", display: "flex", alignItems: "center", gap: 4, opacity: 0.7 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                  <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                </svg>
                {p.sharedCount > 0 && <span style={{ fontSize: 11 }}>{p.sharedCount}</span>}
              </button>
            </div>
            {p.img && <img src={p.img} alt="" onClick={() => setLightboxSrc(p.img)} className="sl-post-img" style={{ width: "100%", maxHeight: 280, objectFit: "cover", borderRadius: 10, marginBottom: 8, border: `1px solid ${_T.cardBorderBot}`, cursor: "pointer", display: "block" }} />}
            {p.text && <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.7, marginBottom: 8 }}>{p.text}</div>}
            {/* Shared/reposted original post preview */}
            {p.sharedPost && (
              <div style={{ background: _T.bg3, borderRadius: 10, padding: 12, marginBottom: 8, border: `1px solid ${_T.cardBorderBot}`, borderLeft: `3px solid ${_T.accent}` }}>
                <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>🔁 {p.sharedPost.user?.name}</div>
                {p.sharedPost.img && <img src={p.sharedPost.img} alt="" style={{ width: '100%', borderRadius: 8, marginBottom: 6, maxHeight: 160, objectFit: 'cover' }} />}
                {p.sharedPost.text && <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.5 }}>{p.sharedPost.text}</div>}
              </div>
            )}
            {p.feeling && (() => {
              const isOwn = p.userId === currentUserId;
              const resonanceOpen = openResonancePosts.has(p.id);
              const f = FEELINGS.find(x => x.label === p.feeling.label) || p.feeling;
              return (
                <>
                  <div
                    onClick={() => !isOwn && toggleResonance(p.id)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, background: resonanceOpen ? `${_T.accent}25` : `${_T.accent}15`, border: `1px solid ${resonanceOpen ? _T.accent : _T.accent + '44'}`, borderRadius: 20, padding: "5px 12px", marginBottom: 8, fontSize: 12, cursor: isOwn ? "default" : "pointer", transition: "all 0.15s" }}>
                    <span>{f.emoji}</span>
                    <span style={{ color: _T.accent, fontWeight: 600 }}>{nl ? f.labelNL : f.label}</span>
                    {!isOwn && <span style={{ fontSize: 10, color: C.silver, marginLeft: 2 }}>{resonanceOpen ? '▲' : '▼'}</span>}
                    {p.resonanceCount > 0 && (
                      <>
                        <span style={{ fontSize: 10, color: _T.accent, marginLeft: 4 }}>⌀{p.resonanceAvg}%</span>
                        {(p.topResonators || []).length > 0 && (() => {
                          const visible = (p.topResonators || []).slice(0, 5);
                          const overflow = p.resonanceCount - visible.length;
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 4 }}>
                              {visible.map((u, i) => (
                                u.picture
                                  ? <img key={u.id} src={u.picture} alt={u.name} title={u.name}
                                      style={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${_T.bg2}`, objectFit: "cover", marginLeft: i === 0 ? 0 : -6, zIndex: 5 - i }} />
                                  : <span key={u.id} title={u.name}
                                      style={{ width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${_T.bg2}`, background: _T.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#000", marginLeft: i === 0 ? 0 : -6, zIndex: 5 - i }}>
                                      {u.name?.[0]?.toUpperCase() || '?'}
                                    </span>
                              ))}
                              {overflow > 0 && <span style={{ fontSize: 10, color: C.silver, marginLeft: 5 }}>+{overflow}</span>}
                            </span>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  {!isOwn && resonanceOpen && (
                    <ResonanceBar
                      key={`${p.id}-${p.myResonance}`}
                      postId={p.id}
                      feeling={p.feeling}
                      postUserId={p.userId}
                      currentUserId={currentUserId}
                      lang={lang}
                      initialMyValue={p.myResonance ?? null}
                      initialAvg={p.resonanceAvg ?? null}
                      initialCount={p.resonanceCount ?? 0}
                      onUpdate={handleResonanceUpdate}
                    />
                  )}
                </>
              );
            })()}
            {(() => {
              const commentCount = (() => { try { return JSON.parse(localStorage.getItem(`sl_comments_${p.id}`) || '[]').length; } catch { return 0; } })();
              const commentsOpen = openCommentPosts.has(p.id);
              return (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, paddingTop: 8, borderTop: `1px solid ${_T.cardBorderBot}` }}>
                    {/* Like button — left */}
                    <button onClick={async () => {
                      const wasLiked = liked.includes(p.id);
                      setLiked(wasLiked ? liked.filter(x => x !== p.id) : [...liked, p.id]);
                      setFeedPosts(prev => prev.map(x => x.id === p.id ? { ...x, likes: Math.max(0, (x.likes || 0) + (wasLiked ? -1 : 1)) } : x));
                      const res = await togglePostLike(p.id);
                      if (res && typeof res.likes === "number") {
                        setFeedPosts(prev => prev.map(x => x.id === p.id ? { ...x, likes: res.likes } : x));
                      }
                      if (!wasLiked && p.userId !== currentUserId) {
                        trackSocialEvent('post_like', { targetId: p.userId, weight: 2 });
                      }
                    }} style={{ background: "none", border: "none", color: isLiked ? C.red : C.silver, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
                      {isLiked ? "❤️" : "🤍"} {p.likes || 0}
                    </button>
                    {/* Comment toggle — right */}
                    <button onClick={() => toggleComments(p.id)} style={{ background: "none", border: "none", color: commentsOpen ? _T.accent : C.silver, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                      💬 {commentCount > 0 ? `${commentCount} ${nl ? "reacties" : "comments"}` : (nl ? "Reageer" : "Comment")}
                    </button>
                  </div>
                  {commentsOpen && <div style={{ height: 10 }} />}
                  <CommentBox
                    postId={p.id}
                    currentUserId={currentUserId}
                    userName={userName}
                    friends={friends}
                    allUsers={allUsers}
                    lang={lang}
                    nl={nl}
                    open={commentsOpen}
                  />
                </>
              );
            })()}
          </div>
        );
      })}
    </div>
  );

  if (activeTab === "profile" && profileTarget) return (
    <>
      <ProfilePage user={profileTarget} lang={lang} currentUserId={currentUserId} friends={friends} friendRequests={friendRequests} onFriendRequest={onFriendRequest} onBack={() => { setProfileTarget(null); setActiveTab("timeline"); }} onOpenChat={() => setActiveTab("chat")} onAcceptFriend={onAcceptFriend} onDeclineFriend={onDeclineFriend} myPosts={myPosts} allUsers={allUsers} onPlayTogether={onPlayTogether} onViewBizPage={onViewBizPage} />
      <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </>
  );

  const feedSidebar = (
    <aside className="sl-feed-sidebar">
      {/* Friends panel */}
      <div style={{background:_T.bg3, border:`1px solid ${_T.cardBorderTop}`, borderRadius:16, padding:16}}>
        <div style={{fontSize:11,color:_T.accent,fontWeight:700,letterSpacing:2,marginBottom:12}}>
          👥 {nl ? 'VRIENDEN' : 'FRIENDS'} {(friends||[]).length > 0 && <span style={{color:C.silver,fontWeight:400}}>({(friends||[]).length})</span>}
        </div>
        {(friends||[]).length === 0 ? (
          <div style={{color:C.silver,fontSize:12,lineHeight:1.6,textAlign:'center',padding:'8px 0'}}>
            {nl?'Voeg vrienden toe om ze hier te zien.':'Add friends to see them here.'}
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {(friends||[]).slice(0,10).map(f => (
              <div key={f.id} onClick={()=>openProfile(f)}
                style={{display:'flex',alignItems:'center',gap:10,padding:'8px 6px',borderRadius:10,cursor:'pointer',transition:'background 0.15s'}}
                onMouseEnter={e=>e.currentTarget.style.background=`${_T.accent}0a`}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{position:'relative',flexShrink:0}}>
                  <Avatar user={f} size={34}/>
                  {!f.isResting && <div style={{position:'absolute',bottom:1,right:1,width:8,height:8,borderRadius:'50%',background:'#4AC87A',border:`2px solid ${_T.bg3}`}}/>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:C.white,fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.name}</div>
                  <div style={{fontSize:11,color:f.isResting?C.silver:'#4AC87A'}}>
                    {f.isResting ? (nl?'Aan het rusten':'Resting') : (nl?'Online':'Online')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Community stats */}
      <div style={{background:_T.bg3, border:`1px solid ${_T.cardBorderTop}`, borderRadius:16, padding:16}}>
        <div style={{fontSize:11,color:_T.accent,fontWeight:700,letterSpacing:2,marginBottom:12}}>📊 {nl?'ACTIVITEIT':'ACTIVITY'}</div>
        {[
          {icon:'📝', label:nl?'Berichten':'Posts',   val:feedPosts.length},
          {icon:'👥', label:nl?'Vrienden':'Friends', val:(friends||[]).length},
        ].map(s=>(
          <div key={s.label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:`1px solid ${_T.cardBorderBot}`}}>
            <span style={{color:C.silver,fontSize:13}}>{s.icon} {s.label}</span>
            <span style={{color:C.white,fontWeight:700,fontSize:14}}>{s.val}</span>
          </div>
        ))}
      </div>
    </aside>
  );

  return (
    <div className="fadeUp">
      {header}
      {tabBar}
      {activeTab === "timeline" && (
        <div className="sl-feed-layout">
          <div>{timeline}</div>
          {feedSidebar}
        </div>
      )}
      {activeTab === "friends_tab" && <FriendsScreen lang={lang} currentUserId={currentUserId} friends={friends} friendRequests={friendRequests} allUsers={allUsers} onFriendRequest={onFriendRequest} onAcceptFriend={onAcceptFriend} onDeclineFriend={onDeclineFriend} onOpenProfile={openProfile} onOpenChat={u => { if (!u.isResting) setActiveTab("chat"); }} />}
      {activeTab === "chat" && <ChatScreen lang={lang} currentUser={currentUser} allUsers={allUsers} friends={friends} conversations={conversations} setConversations={setConversations} onOpenProfile={openProfile} />}
      {activeTab === "groups" && <FocusGroupsTab lang={lang} currentUser={currentUser} onAddNotification={onAddNotification} onNav={onNav} />}
      {activeTab === "scores" && <ScoresTab lang={lang} allUsers={allUsers} onOpenProfile={openProfile} />}
      <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />

      {/* Share modal — portalled to document.body to escape CSS transform ancestors */}
      {shareTarget && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: _T.bg2, borderRadius: 18, padding: 20, width: '100%', maxWidth: 480, border: `1px solid ${_T.cardBorderTop}`, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={_T.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
              </svg>
              <span style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>{nl ? 'Post delen' : 'Share post'}</span>
            </div>
            <div style={{ background: _T.bg3, borderRadius: 12, padding: 12, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}`, borderLeft: `3px solid ${_T.accent}` }}>
              <div style={{ color: C.silver, fontSize: 11, fontWeight: 600, marginBottom: 6 }}>{shareTarget.user?.name}</div>
              {shareTarget.img && <img src={shareTarget.img} alt="" style={{ width: '100%', borderRadius: 8, marginBottom: 8, maxHeight: 140, objectFit: 'cover' }} />}
              {shareTarget.text && <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.5 }}>{shareTarget.text.slice(0, 160)}{shareTarget.text.length > 160 ? '…' : ''}</div>}
            </div>
            <textarea value={shareText} onChange={e => setShareText(e.target.value)}
              placeholder={nl ? 'Voeg een reactie toe… (optioneel)' : 'Add a comment… (optional)'}
              style={{ width: '100%', boxSizing: 'border-box', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none', minHeight: 72, resize: 'none', marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShareTarget(null); setShareText(''); }}
                style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '9px 18px', cursor: 'pointer' }}>
                {nl ? 'Annuleren' : 'Cancel'}
              </button>
              <button onClick={handleShare} disabled={sharing}
                style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '9px 22px', cursor: 'pointer', opacity: sharing ? 0.6 : 1 }}>
                {sharing ? '…' : (nl ? 'Delen' : 'Share')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default CommunityScreen;
